/**
 * MODULE: _shared/frontend-shell/src/features/crm/components/shared/FilterBar.tsx
 * PURPOSE: CRM filter row — md+ inline controls; below md collapses to Filters pill + bottom sheet (dashboard pattern)
 * DEPENDENCIES: react, flowbite-react, react-router-dom, lucide-react, ../../../../layout/mobileShell, ../../../../pages/dashboard/useBodyScrollLock, ../../hooks/useCrmSavedViews, ../../utils/crmFilterUrl, ../../types/crm.types
 * PUBLIC_API: FilterBar, FilterBarProps, FilterBarField
 * SIDE_EFFECTS: Calls onFilterChange from controls; Reset clears the URL query via navigate replace then onReset
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 611/620
 *
 * KEYBOARD — Tab order (audited 2026-05-08): Desktop follows DOM order — optional Views or Save chips first,
 * then **`CrmFilterBarFields`** (**search** when in **`fields`**, then remaining keys in **`fields`** array order),
 * then Reset when dirty, then save block. No **`tabIndex={-1}`** on filter inputs. Mobile uses a Filters sheet;
 * see **`src/features/crm/README.md`** — **Keyboard behaviors (CRM shell audit)**.
 * RECENT: 2026-05-11 Task 8 #91 — Views menu footer separator + **`+ Save current view`** (name + submit) on all CRM tabs with **`savedViewsEntity`** (plain **`hr`**, not Flowbite **`DropdownDivider`** — no **`Dropdown`** context)
 * RECENT_PRIOR: 2026-05-08 [2.2.890] Doc pointer — keyboard audit lives in CRM **`README.md`** — root **`CHANGELOG` [2.2.890]`**
 * RECENT_PRIOR: 2026-04-30 [2.2.758] Saved views chip + save popover (`useCrmSavedViews`, **`crm-save-view-trigger`**) — CHANGELOG [2.2.758]. [2.2.752] Mobile bottom sheet — CHANGELOG [2.2.752]. [2.2.748] Reset URL
 */

import { Badge, Button, Label, Select, TextInput } from 'flowbite-react';
import { Bookmark, ChevronDown, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMobileShell } from '../../../../layout/mobileShell';
import { useBodyScrollLock } from '../../../../pages/dashboard/useBodyScrollLock';
import type { CrmFilterState } from '../../types/crm.types';
import { useCrmSavedViews, type CrmSavedView, type CrmSavedViewsEntity } from '../../hooks/useCrmSavedViews';
import { useCrmTerritoryOptions } from '../../hooks/useCrmTerritoryOptions';
import { CRM_DEAL_STAGE_SELECT_OPTIONS } from '../../constants/crmDealStages';
import { TASK_STATUS_LABELS } from '../../types/task.types';
import { applySavedFilterRecordToSearchParams } from '../../utils/crmFilterUrl';

export type FilterBarField = 'search' | 'owner' | 'status' | 'stage' | 'territory' | 'dateRange';

export type FilterBarProps = {
  filters: CrmFilterState;
  onFilterChange: (key: keyof CrmFilterState, value: string) => void;
  onReset: () => void;
  fields?: FilterBarField[];
  placeholder?: string;
  /** Merged onto the root bar (e.g. width constraints in `CrmHeader`). */
  className?: string;
  /** When false, field labels are screen-reader only (compact toolbar). */
  showLabels?: boolean;
  /** When `fields` includes `status`, overrides default task-style status options (e.g. Contacts active or inactive). */
  statusOptions?: { value: string; label: string }[];
  /** When set, shows Views chip and Save view (URL-backed filters POST to `/api/v1/crm/saved-views`). */
  savedViewsEntity?: CrmSavedViewsEntity;
  /** When `fields` includes **`owner`**, overrides static demo IDs (deals or contacts from API-derived labels). */
  ownerOptions?: { value: string; label: string }[];
};

const DEFAULT_FIELDS: FilterBarField[] = [
  'search',
  'owner',
  'status',
  'stage',
  'territory',
  'dateRange',
];

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'open', label: 'Open' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'complete', label: 'Complete' },
  { value: 'overdue', label: TASK_STATUS_LABELS.overdue },
  { value: 'todo', label: TASK_STATUS_LABELS.todo },
  { value: 'in_progress', label: TASK_STATUS_LABELS.in_progress },
  { value: 'done', label: TASK_STATUS_LABELS.done },
];

export const STAGE_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All stages' },
  ...CRM_DEAL_STAGE_SELECT_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
];

export const OWNER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All owners' },
  { value: 'user-1', label: 'Morgan Lee' },
  { value: 'user-2', label: 'Alex Kim' },
  { value: 'user-3', label: 'Jordan Patel' },
];

const MOCK_TERRITORY_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All territories' },
  { value: 'terr-west', label: 'West' },
  { value: 'terr-east', label: 'East' },
  { value: 'terr-central', label: 'Central' },
];

function hasActiveFilters(filters: CrmFilterState, fields: FilterBarField[]): boolean {
  return countActiveFilters(filters, fields) > 0;
}

function countActiveFilters(filters: CrmFilterState, fields: FilterBarField[]): number {
  let n = 0;
  if (fields.includes('search') && filters.search.trim() !== '') n += 1;
  if (fields.includes('owner') && Boolean(filters.owner)) n += 1;
  if (fields.includes('status') && Boolean(filters.status)) n += 1;
  if (fields.includes('stage') && Boolean(filters.stage)) n += 1;
  if (fields.includes('territory') && Boolean(filters.territory)) n += 1;
  if (fields.includes('dateRange') && Boolean(filters.dateRange?.from || filters.dateRange?.to)) n += 1;
  return n;
}

function emitDateRange(
  filters: CrmFilterState,
  onFilterChange: FilterBarProps['onFilterChange'],
  patch: Partial<{ from: string; to: string }>
) {
  const from = patch.from !== undefined ? patch.from : (filters.dateRange?.from ?? '');
  const to = patch.to !== undefined ? patch.to : (filters.dateRange?.to ?? '');
  if (!from.trim() && !to.trim()) {
    onFilterChange('dateRange', '');
    return;
  }
  onFilterChange('dateRange', JSON.stringify({ from, to }));
}

type FieldsProps = {
  filters: CrmFilterState;
  onFilterChange: FilterBarProps['onFilterChange'];
  fields: FilterBarField[];
  placeholder: string;
  showLabels: boolean;
  statusChoices: { value: string; label: string }[];
  ownerChoices: { value: string; label: string }[];
  territoryChoices: { value: string; label: string }[];
  /** Suffix for input ids when both inline and sheet could mount (avoid duplicates). */
  idSuffix?: string;
  /** Bottom sheet stacks fields vertically; toolbar matches inline filter bar layout. */
  layout?: 'toolbar' | 'sheet';
};

function CrmFilterBarFields({
  filters,
  onFilterChange,
  fields,
  placeholder,
  showLabels,
  statusChoices,
  ownerChoices,
  territoryChoices,
  idSuffix = '',
  layout = 'toolbar',
}: FieldsProps) {
  const selectFields = fields.filter((f) => f !== 'search' && f !== 'dateRange');
  const labelClass = showLabels ? 'mb-1 block' : 'sr-only';
  const suf = idSuffix;
  const isSheet = layout === 'sheet';
  const selectCellClass = isSheet ? 'min-w-0 w-full' : 'w-full sm:w-36 shrink-0';

  if (isSheet) {
    return (
      <div className="flex min-w-0 flex-1 flex-col gap-4">
        {fields.includes('search') ? (
          <div className="min-w-0 w-full">
            <Label htmlFor={`crm-filter-search${suf}`} className={labelClass}>
              Search
            </Label>
            <TextInput
              id={`crm-filter-search${suf}`}
              data-testid="crm-filter-search-input"
              type="search"
              sizing="sm"
              value={filters.search}
              placeholder={placeholder}
              className="w-full"
              aria-label={showLabels ? undefined : placeholder}
              onChange={(e) => onFilterChange('search', e.target.value)}
            />
          </div>
        ) : null}

        {selectFields.length > 0 ? (
          <div className="flex flex-col gap-3">
            {selectFields.includes('owner') ? (
              <div className={selectCellClass}>
                <Label htmlFor={`crm-filter-owner${suf}`} className={labelClass}>
                  Owner
                </Label>
                <Select
                  id={`crm-filter-owner${suf}`}
                  sizing="sm"
                  className="w-full"
                  value={filters.owner ?? ''}
                  onChange={(e) => onFilterChange('owner', e.target.value)}
                >
                  {ownerChoices.map((o) => (
                    <option key={o.value || 'all'} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </div>
            ) : null}

            {selectFields.includes('status') ? (
              <div className={selectCellClass}>
                <Label htmlFor={`crm-filter-status${suf}`} className={labelClass}>
                  Status
                </Label>
                <Select
                  id={`crm-filter-status${suf}`}
                  sizing="sm"
                  className="w-full"
                  value={filters.status ?? ''}
                  onChange={(e) => onFilterChange('status', e.target.value)}
                >
                  {statusChoices.map((o) => (
                    <option key={o.value || 'all'} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </div>
            ) : null}

            {selectFields.includes('stage') ? (
              <div className={selectCellClass}>
                <Label htmlFor={`crm-filter-stage${suf}`} className={labelClass}>
                  Stage
                </Label>
                <Select
                  id={`crm-filter-stage${suf}`}
                  sizing="sm"
                  className="w-full"
                  value={filters.stage ?? ''}
                  onChange={(e) => onFilterChange('stage', e.target.value)}
                >
                  {STAGE_OPTIONS.map((o) => (
                    <option key={o.value || 'all'} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </div>
            ) : null}

            {selectFields.includes('territory') ? (
              <div className={selectCellClass}>
                <Label htmlFor={`crm-filter-territory${suf}`} className={labelClass}>
                  Territory
                </Label>
                <Select
                  id={`crm-filter-territory${suf}`}
                  sizing="sm"
                  className="w-full"
                  value={filters.territory ?? ''}
                  onChange={(e) => onFilterChange('territory', e.target.value)}
                >
                  {territoryChoices.map((o) => (
                    <option key={o.value || 'all'} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
              </div>
            ) : null}
          </div>
        ) : null}

        {fields.includes('dateRange') ? (
          <div className="flex w-full flex-wrap items-end gap-2">
            <div className="min-w-0 flex-1">
              <Label htmlFor={`crm-filter-from${suf}`} className={labelClass}>
                From
              </Label>
              <TextInput
                id={`crm-filter-from${suf}`}
                type="date"
                sizing="sm"
                className="w-full min-w-[10rem]"
                value={filters.dateRange?.from ?? ''}
                onChange={(e) => emitDateRange(filters, onFilterChange, { from: e.target.value })}
              />
            </div>
            <div className="min-w-0 flex-1">
              <Label htmlFor={`crm-filter-to${suf}`} className={labelClass}>
                To
              </Label>
              <TextInput
                id={`crm-filter-to${suf}`}
                type="date"
                sizing="sm"
                className="w-full min-w-[10rem]"
                value={filters.dateRange?.to ?? ''}
                onChange={(e) => emitDateRange(filters, onFilterChange, { to: e.target.value })}
              />
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
      {fields.includes('search') ? (
        <div className="min-w-0 w-full flex-1 sm:min-w-[12rem]">
          <Label htmlFor={`crm-filter-search${suf}`} className={labelClass}>
            Search
          </Label>
          <TextInput
            id={`crm-filter-search${suf}`}
            data-testid="crm-filter-search-input"
            type="search"
            sizing="sm"
            value={filters.search}
            placeholder={placeholder}
            className="w-full"
            aria-label={showLabels ? undefined : placeholder}
            onChange={(e) => onFilterChange('search', e.target.value)}
          />
        </div>
      ) : null}

      {selectFields.map((field) => {
        if (field === 'owner') {
          return (
            <div key="owner" className={selectCellClass}>
              <Label htmlFor={`crm-filter-owner${suf}`} className={labelClass}>
                Owner
              </Label>
              <Select
                id={`crm-filter-owner${suf}`}
                sizing="sm"
                className="w-full"
                value={filters.owner ?? ''}
                onChange={(e) => onFilterChange('owner', e.target.value)}
              >
                {ownerChoices.map((o) => (
                  <option key={o.value || 'all'} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </div>
          );
        }
        if (field === 'status') {
          return (
            <div key="status" className={selectCellClass}>
              <Label htmlFor={`crm-filter-status${suf}`} className={labelClass}>
                Status
              </Label>
              <Select
                id={`crm-filter-status${suf}`}
                sizing="sm"
                className="w-full"
                value={filters.status ?? ''}
                onChange={(e) => onFilterChange('status', e.target.value)}
              >
                {statusChoices.map((o) => (
                  <option key={o.value || 'all'} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </div>
          );
        }
        if (field === 'stage') {
          return (
            <div key="stage" className={selectCellClass}>
              <Label htmlFor={`crm-filter-stage${suf}`} className={labelClass}>
                Stage
              </Label>
              <Select
                id={`crm-filter-stage${suf}`}
                sizing="sm"
                className="w-full"
                value={filters.stage ?? ''}
                onChange={(e) => onFilterChange('stage', e.target.value)}
              >
                {STAGE_OPTIONS.map((o) => (
                  <option key={o.value || 'all'} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </div>
          );
        }
        if (field === 'territory') {
          return (
            <div key="territory" className={selectCellClass}>
              <Label htmlFor={`crm-filter-territory${suf}`} className={labelClass}>
                Territory
              </Label>
              <Select
                id={`crm-filter-territory${suf}`}
                sizing="sm"
                className="w-full"
                value={filters.territory ?? ''}
                onChange={(e) => onFilterChange('territory', e.target.value)}
              >
                {territoryChoices.map((o) => (
                  <option key={o.value || 'all'} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </div>
          );
        }
        return null;
      })}

      {fields.includes('dateRange') ? (
        <div className="flex w-full min-w-0 flex-1 flex-wrap items-center gap-2 sm:w-auto sm:flex-none">
          <div className="min-w-[10rem] flex-1 sm:flex-none">
            <Label htmlFor={`crm-filter-from${suf}`} className={labelClass}>
              From
            </Label>
            <TextInput
              id={`crm-filter-from${suf}`}
              type="date"
              sizing="sm"
              className="w-full"
              value={filters.dateRange?.from ?? ''}
              onChange={(e) => emitDateRange(filters, onFilterChange, { from: e.target.value })}
            />
          </div>
          <div className="min-w-[10rem] flex-1 sm:flex-none">
            <Label htmlFor={`crm-filter-to${suf}`} className={labelClass}>
              To
            </Label>
            <TextInput
              id={`crm-filter-to${suf}`}
              type="date"
              sizing="sm"
              className="w-full"
              value={filters.dateRange?.to ?? ''}
              onChange={(e) => emitDateRange(filters, onFilterChange, { to: e.target.value })}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function useCrmFilterBarSavedUi(entity: CrmSavedViewsEntity | undefined, dirty: boolean) {
  const { views, saveView, deleteView, isLoading } = useCrmSavedViews(entity ?? 'deals', {
    enabled: Boolean(entity),
  });
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [viewsOpen, setViewsOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  /** Inline name field for the Views dropdown footer ("+ Save current view"). */
  const [viewsMenuSaveName, setViewsMenuSaveName] = useState('');
  const viewsRef = useRef<HTMLDivElement>(null);
  const saveRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!viewsOpen && !saveOpen) return;
    const close = (e: MouseEvent) => {
      const t = e.target as Node;
      if (viewsRef.current?.contains(t) || saveRef.current?.contains(t)) return;
      setViewsOpen(false);
      setSaveOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [viewsOpen, saveOpen]);

  if (!entity) {
    return { viewsBlock: null as ReactNode, saveBlock: null as ReactNode };
  }

  const applyView = (view: CrmSavedView) => {
    const sp = applySavedFilterRecordToSearchParams(entity, view.filters);
    const q = sp.toString();
    navigate({ pathname, search: q ? `?${q}` : '' }, { replace: true });
    setViewsOpen(false);
  };

  const submitSave = async () => {
    const n = saveName.trim();
    if (!n) return;
    try {
      await saveView(n);
      setSaveName('');
      setSaveOpen(false);
    } catch {
      /* keep popover */
    }
  };

  const submitSaveFromViewsMenu = async () => {
    const n = viewsMenuSaveName.trim();
    if (!n) return;
    try {
      await saveView(n);
      setViewsMenuSaveName('');
      setViewsOpen(false);
    } catch {
      /* keep menu open */
    }
  };

  const viewsBlock = (
    <div className="relative shrink-0" ref={viewsRef}>
      <Button
        type="button"
        color="light"
        data-testid="crm-saved-views-trigger"
        className="inline-flex min-h-[44px] items-center gap-1 rounded-full border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-600"
        onClick={() => {
          setSaveOpen(false);
          setViewsOpen((o) => !o);
        }}
      >
        Views
        <ChevronDown className="h-4 w-4 opacity-70" aria-hidden />
      </Button>
      {viewsOpen ? (
        <div
          className="absolute left-0 z-50 mt-1 w-72 max-w-[90vw] rounded-lg border border-gray-200 bg-white py-2 shadow-lg dark:border-gray-600 dark:bg-gray-800"
          role="menu"
        >
          {isLoading ? (
            <div className="px-3 py-2 text-sm text-gray-500">Loading…</div>
          ) : views.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-500">No saved views</div>
          ) : (
            <ul className="max-h-64 overflow-y-auto">
              {views.map((v) => (
                <li key={v.id} data-testid={`crm-saved-view-row-${v.id}`} className="flex items-center gap-1 px-2">
                  <button
                    type="button"
                    className="min-w-0 flex-1 truncate py-2 text-left text-sm text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700"
                    onClick={() => applyView(v)}
                  >
                    {v.name}
                  </button>
                  <button
                    type="button"
                    className="shrink-0 rounded p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                    data-testid={`crm-saved-view-delete-${v.id}`}
                    aria-label={`Delete saved view ${v.name}`}
                    onClick={() => void deleteView(v.id)}
                  >
                    <X className="h-4 w-4" aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <hr className="my-1 border-gray-200 dark:border-gray-600" role="separator" />
          <div className="space-y-2 px-3 pb-1 pt-2">
            <Label htmlFor="crm-views-menu-save-name" className="text-xs font-medium text-gray-600 dark:text-gray-300">
              View name
            </Label>
            <TextInput
              id="crm-views-menu-save-name"
              data-testid="crm-views-menu-save-name-input"
              value={viewsMenuSaveName}
              onChange={(e) => setViewsMenuSaveName(e.target.value)}
              className="input-dark"
              placeholder="e.g. Q2 West"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void submitSaveFromViewsMenu();
                }
              }}
            />
            <Button
              type="button"
              size="sm"
              className="w-full"
              data-testid="crm-views-menu-save-current-view"
              onClick={() => void submitSaveFromViewsMenu()}
            >
              + Save current view
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );

  const saveBlock =
    dirty ? (
      <div className="relative shrink-0" ref={saveRef}>
        <Button
          type="button"
          color="light"
          data-testid="crm-save-view-trigger"
          className="inline-flex min-h-[44px] items-center gap-1 rounded-full border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-600"
          onClick={() => {
            setViewsOpen(false);
            setSaveOpen((o) => !o);
          }}
          aria-label="Save view"
        >
          <Bookmark className="h-4 w-4" aria-hidden />
          <span className="hidden sm:inline">Save view</span>
        </Button>
        {saveOpen ? (
          <div className="absolute left-0 z-50 mt-1 w-64 max-w-[90vw] rounded-lg border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-600 dark:bg-gray-800">
            <Label htmlFor="crm-save-view-name" className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
              View name
            </Label>
            <TextInput
              id="crm-save-view-name"
              data-testid="crm-save-view-name-input"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              className="input-dark mb-2"
              placeholder="e.g. Q2 West"
            />
            <Button type="button" size="sm" data-testid="crm-save-view-submit" onClick={() => void submitSave()}>
              Save
            </Button>
          </div>
        ) : null}
      </div>
    ) : null;

  return { viewsBlock, saveBlock };
}

export function FilterBar({
  filters,
  onFilterChange,
  onReset,
  fields = DEFAULT_FIELDS,
  placeholder = 'Search…',
  className = '',
  showLabels = true,
  statusOptions,
  savedViewsEntity,
  ownerOptions,
}: FilterBarProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isMobileShell } = useMobileShell();
  const [sheetOpen, setSheetOpen] = useState(false);
  const statusChoices = statusOptions ?? STATUS_OPTIONS;
  const ownerChoices = ownerOptions ?? OWNER_OPTIONS;
  const { options: territoryHookOptions, isLive: hasLiveTerritories } = useCrmTerritoryOptions();
  const territoryChoices = useMemo(() => {
    const live = territoryHookOptions.filter((o) => o.value !== '');
    if (!hasLiveTerritories || live.length === 0) {
      return MOCK_TERRITORY_FILTER_OPTIONS;
    }
    return [{ value: '', label: 'All territories' }, ...live];
  }, [hasLiveTerritories, territoryHookOptions]);
  const dirty = hasActiveFilters(filters, fields);
  const activeCount = countActiveFilters(filters, fields);
  const savedUi = useCrmFilterBarSavedUi(savedViewsEntity, dirty);

  useBodyScrollLock(isMobileShell && sheetOpen);

  const closeSheet = useCallback(() => setSheetOpen(false), []);

  const resetFilters = useCallback(() => {
    navigate({ pathname, search: '' }, { replace: true });
    onReset();
  }, [navigate, onReset, pathname]);

  if (isMobileShell) {
    return (
      <>
        <div data-testid="crm-filter-bar" className={`flex flex-col gap-2 ${className}`.trim()}>
          <div className="flex flex-wrap items-center gap-2">
            {savedUi.viewsBlock}
            <Button
              type="button"
              color="light"
              data-testid="crm-filter-sheet-trigger"
              aria-expanded={sheetOpen}
              aria-controls="crm-filter-bottom-sheet"
              className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-gray-300 px-4 py-2 text-sm font-medium transition-transform active:scale-95 dark:border-gray-600"
              onClick={() => setSheetOpen(true)}
            >
              Filters
              {activeCount > 0 ? (
                <Badge color="blue" size="sm" data-testid="crm-filter-active-count">
                  {activeCount}
                </Badge>
              ) : null}
            </Button>
            {dirty ? (
              <Button type="button" color="gray" className="min-h-[44px] active:scale-95" onClick={resetFilters}>
                Reset filters
              </Button>
            ) : null}
          </div>
          {savedUi.saveBlock ? <div className="flex justify-end">{savedUi.saveBlock}</div> : null}
        </div>

        <div
          role="presentation"
          className={`fixed inset-0 z-40 bg-black/30 md:hidden ${sheetOpen ? '' : 'pointer-events-none opacity-0'}`}
          aria-hidden={!sheetOpen}
          onClick={closeSheet}
        />
        <div
          id="crm-filter-bottom-sheet"
          role="dialog"
          aria-modal={sheetOpen}
          aria-hidden={!sheetOpen}
          className={`fixed bottom-0 left-0 right-0 z-50 flex max-h-[85dvh] flex-col rounded-t-xl border border-gray-200 bg-white shadow-2xl transition-transform duration-300 ease-out dark:border-gray-700 dark:bg-gray-800 md:hidden ${
            sheetOpen ? 'translate-y-0' : 'pointer-events-none translate-y-full'
          }`}
        >
          <div className="shrink-0 rounded-t-xl bg-white pt-3 dark:bg-gray-800">
            <div className="flex justify-center pb-2">
              <div
                data-testid="crm-filter-sheet-drag-handle"
                className="h-1 w-10 rounded-full bg-gray-300 dark:bg-gray-600"
                aria-hidden
              />
            </div>
            <div className="flex items-center justify-between border-b border-gray-100 px-4 pb-3 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Filters</h2>
              <Button type="button" color="gray" size="xs" className="min-h-[44px] min-w-[44px]" onClick={closeSheet}>
                Done
              </Button>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
            <CrmFilterBarFields
              filters={filters}
              onFilterChange={onFilterChange}
              fields={fields}
              placeholder={placeholder}
              showLabels
              statusChoices={statusChoices}
              ownerChoices={ownerChoices}
              territoryChoices={territoryChoices}
              idSuffix="-sheet"
              layout="sheet"
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <div
      data-testid="crm-filter-bar"
      className={`flex flex-col gap-2 lg:gap-3 ${className}`.trim()}
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        {savedUi.viewsBlock}
        <div className="min-w-0 flex-1">
          <CrmFilterBarFields
            filters={filters}
            onFilterChange={onFilterChange}
            fields={fields}
            placeholder={placeholder}
            showLabels={showLabels}
            statusChoices={statusChoices}
            ownerChoices={ownerChoices}
            territoryChoices={territoryChoices}
          />
        </div>
        {dirty ? (
          <div className="shrink-0 lg:pl-2">
            <Button type="button" color="gray" className="min-h-[44px] active:scale-95" onClick={resetFilters}>
              Reset filters
            </Button>
          </div>
        ) : null}
      </div>
      {savedUi.saveBlock ? <div className="flex justify-end lg:justify-start">{savedUi.saveBlock}</div> : null}
    </div>
  );
}
